import {
    EnvironmentProviders,
    InjectionToken,
    Provider,
    inject,
    provideAppInitializer,
} from '@angular/core';
import { MapLoaderService } from './map-loader.service';

// Configuration token for map loading options
export interface MapLoadingConfig {
    /** Whether to load maps during app initialization (default: true) */
    loadOnInit?: boolean;
    /** Timeout in milliseconds for map loading (default: 10000) */
    timeout?: number;
    /** Whether to fail silently if map loading fails (default: false) */
    failSilently?: boolean;
    /** Libraries to preload during initialization */
    preloadLibraries?: Array<
        | 'core'
        | 'maps'
        | 'places'
        | 'marker'
        | 'geometry'
        | 'geocoding'
        | 'streetView'
        | 'drawing'
        | 'visualization'
    >;
}

export const MAP_LOADING_CONFIG = new InjectionToken<MapLoadingConfig>(
    'MAP_LOADING_CONFIG',
    {
        providedIn: 'root',
        factory: () => ({
            loadOnInit: true,
            timeout: 10000,
            failSilently: false,
            preloadLibraries: [],
        }),
    }
);

export const provideMap = (
    config?: MapLoadingConfig
): Array<Provider | EnvironmentProviders> => {
    const providers: Array<Provider | EnvironmentProviders> = [
        MapLoaderService,
    ];

    // Add config provider if provided
    if (config) {
        providers.push({
            provide: MAP_LOADING_CONFIG,
            useValue: {
                ...{
                    loadOnInit: true,
                    timeout: 10000,
                    failSilently: false,
                    preloadLibraries: [],
                },
                ...config,
            },
        });
    }

    // Add app initializer based on config
    const finalConfig = {
        loadOnInit: true,
        timeout: 10000,
        failSilently: false,
        preloadLibraries: [],
        ...config,
    };

    if (finalConfig.loadOnInit) {
        providers.push(
            provideAppInitializer(() => {
                const mapLoader = inject(MapLoaderService);
                const loadingConfig = inject(MAP_LOADING_CONFIG);

                return new Promise<void>(async (resolve, reject) => {
                    try {
                        // Load the base API
                        await mapLoader.load();

                        // Preload specified libraries
                        if (
                            loadingConfig.preloadLibraries &&
                            loadingConfig.preloadLibraries.length > 0
                        ) {
                            const libraryPromises =
                                loadingConfig.preloadLibraries.map((lib) =>
                                    mapLoader
                                        .loadLibrary(lib)
                                        .catch((error) => {
                                            console.warn(
                                                `⚠️ Failed to preload library '${lib}':`,
                                                error
                                            );
                                            // Don't fail the entire initialization for individual library failures
                                            return null;
                                        })
                                );

                            await Promise.all(libraryPromises);
                        }

                        resolve();
                    } catch (error) {
                        console.error(
                            '❌ Failed to initialize Google Maps API:',
                            error
                        );

                        if (loadingConfig.failSilently) {
                            console.warn(
                                '🤫 Continuing app initialization despite Maps API failure (failSilently: true)'
                            );
                            resolve();
                        } else {
                            reject(error);
                        }
                    }
                });
            })
        );
    }

    return providers;
};

// Convenience function for common configurations
export const provideMapWithPlaces = (): Array<
    Provider | EnvironmentProviders
> => {
    return provideMap({
        preloadLibraries: ['maps', 'places', 'marker'],
    });
};

export const provideMapSilent = (): Array<Provider | EnvironmentProviders> => {
    return provideMap({
        failSilently: true,
        preloadLibraries: ['maps'],
    });
};

export const provideMapLazy = (): Array<Provider | EnvironmentProviders> => {
    return provideMap({
        loadOnInit: false,
    });
};
